// EntropyDashboard.jsx
// ============================================================
// ⚡ EntropyDashboard.jsx — Graphe d'entropie en temps réel
// ============================================================
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/entropy-dashboard.css";

/**
 * Graphe sismographe temps réel + moyenne glissante + tooltip + zoom/pan (overview brush)
 * - Affiche la valeur réelle (aucune triche)
 * - Historique conservé jusqu'à 2000 points
 * - Fenêtre visible contrôlée par un slider ET par un "brush" sur le mini-graphe
 */
export default function EntropyDashboard({ entropy }) {
  // Historique: { value:number, time:string }
  const [history, setHistory] = useState([]);
  const [tooltip, setTooltip] = useState(null);

  // Fenêtre visible (zoom/pan)
  const [windowSize, setWindowSize] = useState(50);   // nombre de points visibles
  const [startIndex, setStartIndex] = useState(0);    // index de début dans l'historique

  // Overview brush (drag)
  const [dragging, setDragging] = useState(false);
  const overviewRef = useRef(null);

  // ===== Ingestion live =====
  useEffect(() => {
    if (entropy !== undefined) {
      const now = new Date().toLocaleTimeString();
      setHistory(prev => {
        const next = [...prev, { value: entropy, time: now }];
        // garder un historique suffisamment long pour le pan
        return next.slice(-2000);
      });
    }
  }, [entropy]);

  // Ajuster startIndex par défaut pour coller à la fin si nouvelle donnée
  useEffect(() => {
    // Si la fenêtre dépasse la taille, recoller automatiquement au bord droit
    if (history.length > 0) {
      const maxStart = Math.max(0, history.length - windowSize);
      if (startIndex > maxStart) setStartIndex(maxStart);
    }
  }, [history.length, windowSize]); // eslint-disable-line

  // ===== Données visibles =====
  const visibleHistory = useMemo(() => {
    const end = Math.min(history.length, startIndex + windowSize);
    return history.slice(startIndex, end);
  }, [history, startIndex, windowSize]);

  const values = visibleHistory.map(h => h.value);

  // ===== Echelle verticale (pour le rendu uniquement) =====
  const minVal = values.length ? Math.min(...values) : 0;
  const maxVal = values.length ? Math.max(...values) : 1;
  const normalizeY = (v) => {
    if (maxVal === minVal) return 100;
    return 200 - ((v - minVal) / (maxVal - minVal)) * 200;
  };

  // ===== Courbe moyenne glissante (5 points) =====
  const movingAvg = (arr, win = 5) =>
    arr.map((_, i) => {
      const slice = arr.slice(Math.max(0, i - win + 1), i + 1);
      return slice.reduce((a, b) => a + b, 0) / slice.length;
    });
  const avgValues = movingAvg(values);

  // ===== Tooltip principal (survol graphe principal) =====
  const handleMainMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = e.clientX - rect.left;

    const spacing = 6; // pixel entre 2 points
    const idx = Math.floor(x / spacing);
    const item = visibleHistory[idx];
    if (!item) return setTooltip(null);

    setTooltip({
      x,
      y: normalizeY(item.value),
      value: item.value,
      time: item.time,
    });
  };
  const handleMainLeave = () => setTooltip(null);

  // ===== Overview (mini-graphe) & brush =====
  // Normalisation pour overview (hauteur 60px)
  const allValues = history.map(h => h.value);
  const oMin = allValues.length ? Math.min(...allValues) : 0;
  const oMax = allValues.length ? Math.max(...allValues) : 1;
  const normalizeOY = (v) => {
    if (oMax === oMin) return 30;
    return 60 - ((v - oMin) / (oMax - oMin)) * 60;
  };

  const overviewSpacing = 2; // plus serré pour overview
  const maxStart = Math.max(0, history.length - windowSize);

  const setStartFromX = (clientX) => {
    const rect = overviewRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const idx = Math.floor(x / overviewSpacing) - Math.floor(windowSize / 2);
    const clamped = Math.max(0, Math.min(idx, maxStart));
    setStartIndex(clamped);
  };

  const handleOverviewDown = (e) => {
    if (!overviewRef.current) return;
    setDragging(true);
    setStartFromX(e.clientX);
  };
  const handleOverviewMove = (e) => {
    if (!dragging) return;
    setStartFromX(e.clientX);
  };
  const handleOverviewUp = () => setDragging(false);
  const handleOverviewLeave = () => setDragging(false);

  // Largeur calculée (en px) pour éviter points tassés
  const mainWidth = Math.max((visibleHistory.length - 1) * 6 + 20, 300);
  const overviewWidth = Math.max((history.length - 1) * overviewSpacing + 20, 300);
  const brushX = startIndex * overviewSpacing;
  const brushW = Math.max(windowSize * overviewSpacing, 10);

  return (
    <div className="entropy-dashboard">
      <h2>⚡ Graphe Entropie (temps réel)</h2>

      {/* Valeur brute (réelle) */}
      <p className="entropy-value">
        Valeur actuelle : <strong>{Number.isFinite(entropy) ? entropy.toFixed(2) : "—"}</strong>
      </p>

      {/* ===== Graphe principal ===== */}
      {/* On ajoute overflow: 'hidden' pour couper tout ce qui dépasse */}
<div className="entropy-graph" onMouseLeave={handleMainLeave} style={{ overflow: 'hidden', borderRadius: '12px', position: 'relative' }}>
        {visibleHistory.length > 1 ? (
          <svg width="100%" height="200" viewBox={`0 0 ${mainWidth} 200`} onMouseMove={handleMainMove}>
            <defs>
              <linearGradient id="entropyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#ff3333" />
                <stop offset="50%" stopColor="#ffaa33" />
                <stop offset="75%" stopColor="#00ff66" />
                <stop offset="100%" stopColor="#33aaff" />
              </linearGradient>
            </defs>

            {/* Courbe brute */}
            <polyline
              fill="none"
              stroke="url(#entropyGradient)"
              strokeWidth="2"
              points={visibleHistory.map((h, i) => `${i * 6},${normalizeY(h.value)}`).join(" ")}
            />

            {/* Courbe moyenne glissante (pointillée) */}
            <polyline
              fill="none"
              stroke="#ffffff"
              strokeDasharray="5,5"
              strokeWidth="1.5"
              points={avgValues.map((v, i) => `${i * 6},${normalizeY(v)}`).join(" ")}
            />

            {/* Tooltip (ligne + point) */}
            {tooltip && (
              <>
                <line x1={tooltip.x} x2={tooltip.x} y1="0" y2="200" stroke="#888" strokeDasharray="4" />
                <circle cx={tooltip.x} cy={tooltip.y} r="4" fill="#ffcc00" stroke="#000" />
              </>
            )}
          </svg>
        ) : (
          <p style={{ color: "#666", textAlign: "center", paddingTop: "80px" }}>
            En attente de données…
          </p>
        )}

        {/* Panneau tooltip */}
        {tooltip && (
          <div className="tooltip" style={{ left: tooltip.x + 10, top: tooltip.y }}>
            <div>🕒 {tooltip.time}</div>
            <div>⚡ Entropie : {tooltip.value.toFixed(2)}</div>
            <div>Σ Score total : {tooltip.value.toFixed(2)}</div>
          </div>
        )}
      </div>

      {/* ===== Controls zoom ===== */}
      <div className="entropy-zoom">
        <label>Fenêtre visible : {windowSize} points</label>
        <input
          id="window-size"
          name="window-size"
          type="range"
          min="20"
          max="200"
          step="10"
          value={windowSize}
          onChange={(e) => {
            const newSize = Number(e.target.value);
            setWindowSize(newSize);
            // recoller la fenêtre à droite si besoin
            const newMaxStart = Math.max(0, history.length - newSize);
            if (startIndex > newMaxStart) setStartIndex(newMaxStart);
          }}
        />
      </div>

      {/* ===== Overview + brush ===== */}
      <div
        className="entropy-overview"
        ref={overviewRef}
        onMouseDown={handleOverviewDown}
        onMouseMove={handleOverviewMove}
        onMouseUp={handleOverviewUp}
        onMouseLeave={handleOverviewLeave}
      >
        {history.length > 1 && (
          <svg width="100%" height="60" viewBox={`0 0 ${overviewWidth} 60`}>
            {/* Courbe globale */}
            <polyline
              fill="none"
              stroke="#6ee7ff"
              strokeWidth="1"
              points={history.map((h, i) => `${i * overviewSpacing},${normalizeOY(h.value)}`).join(" ")}
              opacity="0.8"
            />
            {/* Brush (fenêtre sélectionnée) */}
            <rect
              x={brushX}
              y="0"
              width={brushW}
              height="60"
              fill="rgba(0,255,204,0.15)"
              stroke="#00ffcc"
              strokeWidth="1"
            />
          </svg>
        )}
        {history.length <= 1 && <div className="overview-empty">—</div>}
      </div>
    </div>
  );
}
